import {
  getFieldCrops,
  getFirstInstallationDate,
} from '../../managers/distributionManager';
import {
  getRecommendation,
  isSupportingPlannerByWeeks,
} from '../../managers/fieldManager';
const { plannerType } = require('@managers/planningManager');

var distributionActions = require('@actions/distributionActions');
var {
  setNetworkError,
  clearNetworkError,
} = require('@actions/networkErrorActions');
var distributionManager = require('@managers/distributionManager');
var farmingUnitManager = require('@managers/farmingUnitManager');
var fieldManager = require('@managers/fieldManager');
var sensorsManager = require('@managers/sensorsManager');
var GeneralClasses = require('@model/GeneralClasses');
var { error_message } = require('@managers/helpers');
var {
  getItem,
  setItem,
  removeItem,
} = require('@managers/localStorageManager.jsx');
var { getMixpanelInstance } = require('@managers/mixpanelManager');
var {
  runHistoryLoad,
  clearPendingHistoryLoads,
  pendingFarmingUnitHistoryLoads,
  pendingFieldHistoryLoads,
} = require('../lib/historyLoadCoordination');

// historical_data (crop water availability) is real-time and must never be
// persisted — strip it at every level when serializing distributions for the
// cache. A JSON.stringify replacer does this in one pass without cloning or
// mutating the in-memory tree (which keeps historical_data for the session).
var stringifyDistributionsForPersist = data =>
  JSON.stringify(data, (key, value) =>
    key === 'historical_data' ? undefined : value
  );

// Monotonic request id for load_distributions. Guards against stale responses
// from earlier requests overwriting newer field updates.
let lastLoadDistributionsRequestId = 0;
// While true, selected_entities skips history so farming_unit/field history does
// not compete with the priority GET distribution/{id} on the API connection pool.
let deferHistoryDuringInitialTreeLoad = false;

// In-flight dedup for load_distribution_by_id — concurrent callers (e.g.
// notifications filter + notification click) share one GET per distributionId.
const pendingDistributionTreeLoads = {};

// Distributions with a local write the server has not confirmed back to us yet.
// A bulk sweep carries a snapshot taken when it started, so once a save lands
// mid-sweep that snapshot is older than what we know, and merging it would
// revert the edit on screen until the confirming reload arrives. Held from the
// optimistic patch until that reload settles, either way — see the mutation
// thunks below.
//
// Counted, not a flag: saving two fields in a row is ordinary, and the reloads
// take seconds, so they overlap. The first to settle must not clear the guard
// while a later write is still unconfirmed — protection lifts on the last one.
const pendingLocalWrites = new Map();

const holdLocalWrite = distributionId => {
  pendingLocalWrites.set(
    distributionId,
    (pendingLocalWrites.get(distributionId) || 0) + 1
  );
};

const releaseLocalWrite = distributionId => {
  const outstanding = (pendingLocalWrites.get(distributionId) || 0) - 1;
  if (outstanding > 0) {
    pendingLocalWrites.set(distributionId, outstanding);
  } else {
    pendingLocalWrites.delete(distributionId);
  }
};

const DISTRIBUTIONS_CACHE_KEY = 'DISTRIBUTIONS_DATA';

// Cache freshness. parseDistributionsCache guards on schema version only, and
// every refetch path short-circuits on isFullDistributionTree, so a tree
// hydrated once (e.g. via ensure_all_distributions_loaded) is otherwise served
// from IndexedDB indefinitely — field renames, moved/added sensors and crop
// changes stay stale all session on any non-selected distribution. We keep
// serving the cached tree instantly, but once the cache we painted is older
// than this we revalidate a full tree in the background the next time it is
// requested (stale-while-revalidate).
const DISTRIBUTIONS_CACHE_STALE_MS = 3 * 60 * 60 * 1000; // 3h

// When each distribution tree was last known-good: seeded from the persisted
// cache's savedAt on the cold-start paint, then bumped to Date.now() every time
// a tree comes back from the network. One entry per distribution replaces the
// old single savedAt + "revalidated this session" Set, which could not express
// "painted from cache at boot, then refreshed twenty minutes in" — a tree
// hydrated mid-session was pinned fresh for the rest of the process lifetime.
const distributionTreeFetchedAt = new Map();

const markDistributionTreeFresh = (distributionId, at = Date.now()) => {
  if (distributionId) {
    distributionTreeFetchedAt.set(distributionId, at);
  }
};

const isFullDistributionTreeStale = distributionId => {
  const fetchedAt = distributionTreeFetchedAt.get(distributionId);
  // Unknown age (never painted from cache, never fetched here) → not stale.
  if (fetchedAt == null) {
    return false;
  }
  return Date.now() - fetchedAt > DISTRIBUTIONS_CACHE_STALE_MS;
};

// Derive the lightweight [{id, name}] switcher index from a distributions map,
// sorted by name (tie-break id) to match the backend skeleton ordering. The
// cache no longer stores a separate index — it is derived from the data, and
// there is no separate Redux state slice for it either (see
// selectDistributionsIndex below for the memoized version consumers use).
export const deriveIndexFromDistributions = distributionsMap => {
  if (!distributionsMap) {
    return [];
  }
  return Object.values(distributionsMap)
    .filter(d => d?.id)
    .map(d => ({ id: d.id, name: d.name || '' }))
    .sort(
      (a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
        a.id.localeCompare(b.id)
    );
};

// Single-slot memoized selector for the switcher index. Consumers (dropdowns,
// entity selectors) call this from mapStateToProps/useSelector instead of
// reading a separately-dispatched Redux slice — recomputes only when the
// distributions map reference actually changes, so it stays a stable
// reference across unrelated re-renders (avoids extra renders from a fresh
// array on every store update).
let _distributionsIndexMemoInput = null;
let _distributionsIndexMemoOutput = [];
export const selectDistributionsIndex = distributionsMap => {
  if (distributionsMap === _distributionsIndexMemoInput) {
    return _distributionsIndexMemoOutput;
  }
  _distributionsIndexMemoInput = distributionsMap;
  _distributionsIndexMemoOutput =
    deriveIndexFromDistributions(distributionsMap);
  return _distributionsIndexMemoOutput;
};

const parseDistributionsCache = raw => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    // Schema guard (version, not TTL). Only the distributions map is required;
    // the switcher index is derived from it on read.
    if (
      parsed?.version === 2 &&
      parsed.distributions &&
      Object.keys(parsed.distributions).length
    ) {
      return parsed;
    }
  } catch (e) {
    // Best-effort: unexpected shape → ignore and load from server
  }
  return null;
};

const stringifyDistributionsCache = cachePayload =>
  stringifyDistributionsForPersist(cachePayload);

const resolveCurrentDistributionId = async getState => {
  const state = getState();
  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  return cachedId || null;
};

// Resolve selected distribution id as early as possible for the t=0 by-id fetch.
const resolveSelectedDistIdForFetch = async (
  getState,
  selectedDistributionId,
  cacheReadPromise,
  shouldUseCache
) => {
  if (selectedDistributionId) {
    return selectedDistributionId;
  }

  const state = getState();
  const fromEntities =
    state.distribution_data.selected_entities.distribution?.id;
  if (fromEntities) {
    return fromEntities;
  }

  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  if (cachedId) {
    return cachedId;
  }

  if (shouldUseCache) {
    const raw = await cacheReadPromise;
    const cache = parseDistributionsCache(raw);
    if (cache?.currentId) {
      return cache.currentId;
    }
  }

  return null;
};

const deriveSelectedDistIdFromState = (
  state,
  selectedDistributionId,
  cache,
  currentId
) => {
  const distributionsMap =
    state.distribution_data.distributions?.distributions || {};
  return (
    selectedDistributionId ||
    state.distribution_data.selected_entities.distribution?.id ||
    (cache?.currentId && cache.currentId in distributionsMap
      ? cache.currentId
      : null) ||
    (currentId && currentId in distributionsMap ? currentId : null) ||
    deriveIndexFromDistributions(distributionsMap)[0]?.id ||
    null
  );
};

const mergeSelectedDistributionTree = (
  dispatch,
  getState,
  requestId,
  selectedDistId,
  tree
) => {
  if (
    requestId !== lastLoadDistributionsRequestId ||
    !tree ||
    !selectedDistId
  ) {
    return false;
  }

  const merged = mergeDistributionsResult(getState(), {
    distributions: { [selectedDistId]: tree },
  });
  dispatch(set_all_distributions(merged));
  markDistributionTreeFresh(selectedDistId);

  deferHistoryDuringInitialTreeLoad = false;

  const selectedDist = merged.distributions?.[selectedDistId];
  if (distributionManager.isFullDistributionTree(selectedDist)) {
    dispatch(distributionActions.selected_entities(null, null, null, null));
  }

  schedulePersistDistributionsCache(getState, selectedDistId);

  dispatch(clearNetworkError(false));
  dispatch(set_first_all_distributions_load_status('success'));
  return true;
};

const mergeDistributionsResult = (state, incoming) => {
  const existing = state.distribution_data.distributions?.distributions || {};
  const incomingMap = incoming.distributions || {};
  // Shallow-merge already-parsed distribution trees. Re-running
  // GeneralClasses.Distributions over in-memory trees used to corrupt kc_table
  // month values (API flat data.jan vs client data.byMonth.jan).
  const merged = new GeneralClasses.Distributions({});
  merged.distributions = { ...existing, ...incomingMap };
  return merged;
};

// Smart skeleton merge. The skeleton is the source of truth for structure and
// light fields (name, role) and for membership: new distributions / farming
// units are added, ones that disappeared are pruned, and a changed name/role is
// taken from the skeleton. Already-loaded heavy detail (farming-unit fields,
// sensors, historical_data, and any other distribution attributes) is preserved
// — the skeleton never clobbers a full tree.
const mergeSkeletonIntoDistributions = (existingMap, skeletonList) => {
  const result = {};

  for (const skel of skeletonList) {
    if (!skel?.id) {
      continue;
    }
    const prev = existingMap[skel.id];
    const skelFus = skel.farming_units || {};

    if (!prev) {
      // Brand-new distribution — skeleton only until it is selected/loaded.
      result[skel.id] = { ...skel };
      continue;
    }

    const prevFus = prev.farming_units || {};
    const mergedFus = {};
    for (const fuId in skelFus) {
      const prevFu = prevFus[fuId];
      if (!prevFu) {
        // New farming unit — skeleton only.
        mergedFus[fuId] = { ...skelFus[fuId] };
      } else {
        // Keep loaded detail (fields, sensors, historical_data); take the new
        // light fields (name, role) from the skeleton.
        mergedFus[fuId] = {
          ...prevFu,
          name: skelFus[fuId].name,
          role: skelFus[fuId].role,
        };
      }
    }
    // Farming units present before but absent from the skeleton are pruned.

    result[skel.id] = {
      ...prev,
      name: skel.name,
      role: skel.role,
      farming_units: mergedFus,
    };
  }
  // Distributions present before but absent from the skeleton are pruned.

  return result;
};

// Paint the structural spine — every distribution and farming-unit name, no
// fields — the moment GET distribution/skeleton returns. Returns whether
// anything was painted.
const paintSkeleton = (dispatch, getState, requestId, skeleton) => {
  if (requestId !== lastLoadDistributionsRequestId || !skeleton?.length) {
    return false;
  }
  const existingMap =
    getState().distribution_data.distributions?.distributions || {};
  dispatch(
    set_all_distributions(
      new GeneralClasses.Distributions(
        mergeSkeletonIntoDistributions(existingMap, skeleton)
      )
    )
  );
  return true;
};

// Freshness entries for the distributions actually being written, so the
// persisted map does not accumulate ids that have since disappeared.
const pickFreshnessFor = distributions => {
  const result = {};
  for (const id of Object.keys(distributions)) {
    const at = distributionTreeFetchedAt.get(id);
    if (at != null) {
      result[id] = at;
    }
  }
  return result;
};

const persistDistributionsCache = async (state, currentId) => {
  const distributions =
    state.distribution_data.distributions?.distributions || {};
  if (!Object.keys(distributions).length) {
    return;
  }

  await setItem(
    DISTRIBUTIONS_CACHE_KEY,
    stringifyDistributionsCache({
      version: 2,
      savedAt: Date.now(),
      // When each tree was actually fetched. savedAt describes the write, not
      // the trees: any write re-stamps it, so a tree hydrated an hour earlier
      // would come back looking brand new and skip its refresh. Additive and
      // optional, so the schema version stays put — an older build ignores the
      // field, and a payload written without it falls back to savedAt below.
      fetchedAt: pickFreshnessFor(distributions),
      distributions,
      currentId:
        currentId ||
        state.distribution_data.selected_entities.distribution?.id ||
        deriveIndexFromDistributions(distributions)[0]?.id,
    })
  );
};

// Write the cache off the critical path. Every tree that reaches Redux goes
// through here — the selected one, a by-id load, and the background bulk pass —
// so the whole tree survives to the next session instead of only the branch the
// cold load happened to fetch. Failures are non-fatal: the session keeps working
// from memory and the next hydration retries the write.
const schedulePersistDistributionsCache = (getState, currentId) => {
  setImmediate(async () => {
    try {
      await persistDistributionsCache(getState(), currentId);
    } catch (e) {
      console.error('Failed to cache distributions:', e);
    }
  });
};

// Drop the persisted distributions cache and the in-memory tree map. Units are
// baked into the tree server-side, so on a unit-system change both the
// IndexedDB cache and the Redux map hold trees in the old units and must be
// discarded before the fresh (new-unit) load. Awaiting the removeItem before
// re-loading matters: an in-flight load's setImmediate(persistDistributionsCache)
// could otherwise rewrite the cache after we deleted it.
export var clear_distributions_cache = () => async dispatch => {
  await removeItem(DISTRIBUTIONS_CACHE_KEY);
  // Freshness is about the trees we just discarded. Leaving it behind would mark
  // the refetched (new-unit) trees as already revalidated.
  distributionTreeFetchedAt.clear();
  dispatch(set_all_distributions(null));
};

// Helper function to check if farming unit history is already loading
const isFarmingUnitHistoryLoading = (getState, farmingUnitId) => {
  const farmingUnitLoadingObject =
    getState().distribution_data.farming_unit_load_history;
  return (
    farmingUnitLoadingObject.status === 'init' &&
    farmingUnitLoadingObject.farming_unit_ids.includes(farmingUnitId)
  );
};

// Helper function to dispatch history loads
const dispatchHistoryLoads = (
  dispatch,
  distributionId,
  farmingUnitId,
  fieldId = null
) => {
  dispatch(get_farming_units_history(distributionId, farmingUnitId, null));
  setTimeout(() => {
    dispatch(get_farming_units_recommendations(distributionId, farmingUnitId));
  }, 0);
  if (fieldId) {
    dispatch(get_field_history(distributionId, farmingUnitId, fieldId));
  }
};

// Fire-and-forget full-tree refresh for the selected distribution (master-style:
// paginated .then). Does not block load_distributions or the UI shell.
const refreshSelectedDistributionInBackground = (
  dispatch,
  getState,
  requestId,
  selectedDistId
) => {
  if (!selectedDistId) {
    return Promise.resolve();
  }

  // Returned so the caller can hold the background sweep until this settles.
  return distributionManager.getDistributionById(selectedDistId).then(
    tree => {
      if (
        !mergeSelectedDistributionTree(
          dispatch,
          getState,
          requestId,
          selectedDistId,
          tree
        )
      ) {
        return;
      }
    },
    err => {
      if (requestId !== lastLoadDistributionsRequestId) {
        return;
      }
      deferHistoryDuringInitialTreeLoad = false;
      console.log(err);
      console.error(
        '[Distributions] selected distribution load error:',
        err.message
      );
    }
  );
};

// Hydrate the remaining distributions in the background once the boot path has
// done its work, so switching distributions is instant and the persisted cache
// covers the whole tree next session. Fire-and-forget: the boot path must not
// wait on it and must not fail because of it. The requestId guard drops it when
// a newer load_distributions has already superseded this one.
// `waitFor` holds the sweep until the selected tree has settled. The API fans
// out per distribution, so a five-page sweep running alongside the by-id fetch
// has the two competing for the same tables — slowing down the one request the
// user is actually waiting on. Errors are swallowed: if the selected tree failed
// we still want the sweep, it just must not start early.
const scheduleBackgroundFullHydration = (
  dispatch,
  requestId,
  options,
  waitFor
) => {
  Promise.resolve(waitFor)
    .catch(() => {})
    .then(() =>
      setTimeout(() => {
        if (requestId !== lastLoadDistributionsRequestId) {
          return;
        }
        Promise.resolve(
          dispatch(ensure_all_distributions_loaded(options))
        ).catch(err =>
          console.error(
            '[Distributions] background hydration failed:',
            err.message
          )
        );
      }, 0)
    );
};

// Re-pull the tree after a write, holding the background sweep off this
// distribution until the server confirms the change back to us. Cleared on
// either outcome: a failed reload must not exclude the tree from sweeps
// forever, and leaving it stale there is the right result anyway, since the
// next sweep will pick it up.
const reloadAfterLocalWrite = (dispatch, distribution_id, ...args) => {
  holdLocalWrite(distribution_id);
  return Promise.resolve(dispatch(load_distributions(true, ...args))).finally(
    () => releaseLocalWrite(distribution_id)
  );
};

// The same re-pull for writes made outside this module (the New Season
// editors write through newSeasonActions).
export var reload_after_local_write = distribution_id => dispatch =>
  reloadAfterLocalWrite(dispatch, distribution_id);

// Middleware
export var load_distributions = (
  force_load = false,
  shouldUseCache = false,
  selectedDistributionId = null,
  selectedFarmingUnitId = null,
  selectedFieldId = null
) => {
  return async (dispatch, getState) => {
    let state = getState();

    // Load only once (unless forced). Checked before taking a request id: a
    // no-op call that bumped the counter would invalidate the in-flight load's
    // id and silently cancel its background sweep.
    if (state.distribution_data.distributions && !force_load) {
      return;
    }

    const requestId = ++lastLoadDistributionsRequestId;

    dispatch(set_first_all_distributions_load_status('init'));
    dispatch(set_selected_distribution_network_refresh_status('pending'));
    deferHistoryDuringInitialTreeLoad = true;

    // t=0 — skeleton, cache read, and GET distribution/{id} all start together
    // (same pattern as the old summary path: no await-before-fetch chain).
    const cacheReadPromise = shouldUseCache
      ? getItem(DISTRIBUTIONS_CACHE_KEY)
      : Promise.resolve(null);

    const skeletonPromise = distributionManager
      .getDistributionsSkeleton()
      .catch(err => {
        console.log(err);
        console.error('[Distributions] skeleton error:', err.message);
        return null;
      });

    const selectedDistIdPromise = resolveSelectedDistIdForFetch(
      getState,
      selectedDistributionId,
      cacheReadPromise,
      shouldUseCache
    );

    let cache = null;
    let usableScreen = false;

    const cachePaintPromise = (async () => {
      if (!shouldUseCache) {
        return;
      }
      cache = parseDistributionsCache(await cacheReadPromise);
      if (!cache) {
        return;
      }
      // Age of the trees we are about to paint — drives background revalidation
      // of full trees when the user later requests them (isFullDistributionTreeStale).
      // Prefer each tree's own fetch time; caches written before that field
      // existed fall back to the payload's savedAt. Neither available means we
      // cannot date them, so they stay unrecorded (= not stale).
      Object.keys(cache.distributions).forEach(id => {
        const at = cache.fetchedAt?.[id] ?? cache.savedAt;
        if (at != null) {
          markDistributionTreeFresh(id, at);
        }
      });
      dispatch(
        set_all_distributions(
          new GeneralClasses.Distributions(cache.distributions)
        )
      );
      dispatch(distributionActions.selected_entities(null, null, null, null));
      state = getState();
      const se = state.distribution_data.selected_entities;
      if (se?.distribution?.id && se?.farming_unit?.id) {
        usableScreen = true;
        dispatch(set_first_all_distributions_load_status('success'));
      }
    })();

    // The by-id fetch only earns its place when the cache cannot already put a
    // full tree on screen. On a reload it can, so fetching the selected tree
    // here and then sweeping every distribution in the background would request
    // it twice; the forced sweep revalidates it along with the rest. Waiting on
    // the cache read to decide costs nothing on the cold path it protects —
    // there is no cache to read, so it resolves empty straight away.
    //
    // This must key off the cache we just painted, not merely "a full tree is in
    // Redux". A forced reload (every field / farming-unit / distribution save)
    // already holds a full tree in memory carrying the optimistic patch, and
    // treating that as served would skip the very refetch that confirms the
    // write and persists it — the edit would then survive only in memory and
    // vanish on the next reload.
    const selectedTreePromise = Promise.all([
      selectedDistIdPromise,
      cachePaintPromise,
    ]).then(async ([distId]) => {
      if (!distId) {
        return { distId: null, tree: null, servedFromCache: false };
      }
      const painted =
        cache && !force_load
          ? getState().distribution_data.distributions?.distributions?.[distId]
          : null;
      if (distributionManager.isFullDistributionTree(painted)) {
        return { distId, tree: null, servedFromCache: true };
      }
      try {
        const tree = await distributionManager.getDistributionById(distId);
        return { distId, tree, servedFromCache: false };
      } catch (err) {
        console.log(err);
        console.error(
          '[Distributions] selected distribution load error:',
          err.message
        );
        return { distId, tree: null, servedFromCache: false };
      }
    });

    // The skeleton is the first response that can put a usable shell on screen,
    // so it paints on its own timeline rather than waiting for the much heavier
    // GET distribution/{id}. It does wait for the cache paint, which replaces the
    // whole distributions map and would otherwise clobber it — but that is a
    // local IndexedDB read, not the slow leg.
    const skeletonPaintPromise = Promise.all([
      skeletonPromise,
      cachePaintPromise,
    ]).then(([skeleton]) => {
      const painted = paintSkeleton(dispatch, getState, requestId, skeleton);
      if (painted && !usableScreen) {
        // Resolve the selected distribution off the skeleton so the header and
        // the distribution dropdown render now. selected_entities never commits
        // a skeleton farming unit, and deferHistoryDuringInitialTreeLoad is
        // still true here, so no history fires against fields-less data.
        //
        // Only when nothing is resolved yet. On a forced reload the map is
        // momentarily skeleton-only, and re-resolving would swap a perfectly
        // good selection for a skeleton one — enough for the screen to drop to
        // its loading state and back for no reason. Holding the previous
        // selection is correct: the by-id merge re-resolves it properly.
        if (!getState().distribution_data.selected_entities?.distribution) {
          dispatch(
            distributionActions.selected_entities(null, null, null, null)
          );
        }
        usableScreen = true;
        dispatch(set_first_all_distributions_load_status('success'));
      }
      return painted;
    });

    const [skeletonPainted, treeResult, currentId] = await Promise.all([
      skeletonPaintPromise,
      selectedTreePromise,
      resolveCurrentDistributionId(getState),
    ]);

    if (requestId !== lastLoadDistributionsRequestId) {
      return;
    }

    const {
      distId: fetchedDistId,
      tree: selectedTree,
      servedFromCache,
    } = treeResult;

    state = getState();
    const distributionsMap =
      state.distribution_data.distributions?.distributions || {};
    const selectedDistId = deriveSelectedDistIdFromState(
      state,
      selectedDistributionId,
      cache,
      currentId
    );

    let treeMerged = false;
    if (selectedTree && fetchedDistId) {
      treeMerged = mergeSelectedDistributionTree(
        dispatch,
        getState,
        requestId,
        fetchedDistId,
        selectedTree
      );
    }

    if (!usableScreen && skeletonPainted) {
      usableScreen = true;
    }
    if (!usableScreen && Object.keys(distributionsMap).length) {
      usableScreen = true;
    }

    state = getState();
    if (state.distribution_data.distributions?.distributions) {
      postDistributionLoadAnalytics(state.distribution_data.distributions);
    }

    if (usableScreen && !treeMerged) {
      dispatch(clearNetworkError(false));
      dispatch(set_first_all_distributions_load_status('success'));
    } else if (
      !usableScreen &&
      !skeletonPainted &&
      !Object.keys(distributionsMap).length
    ) {
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(set_first_all_distributions_load_status('fail'));
      dispatch(set_selected_distribution_network_refresh_status('fail'));
    }

    // Set when the selected tree is still being fetched after this point; the
    // background sweep waits on it rather than competing with it.
    let selectedTreeSettled = null;

    if (servedFromCache) {
      // The cache already put the full selected tree on screen and the
      // background sweep below will revalidate it, so there is nothing left to
      // fetch here. Release the history gate that mergeSelectedDistributionTree
      // would normally have released.
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(distributionActions.selected_entities(null, null, null, null));
      dispatch(clearNetworkError(false));
      dispatch(set_first_all_distributions_load_status('success'));
    } else {
      const backgroundRefreshStarted =
        !!selectedDistId && (!treeMerged || selectedDistId !== fetchedDistId);

      // Fallback when id was unknown at t=0 or the parallel fetch failed.
      if (backgroundRefreshStarted) {
        selectedTreeSettled = refreshSelectedDistributionInBackground(
          dispatch,
          getState,
          requestId,
          selectedDistId
        );
      } else if (!treeMerged) {
        deferHistoryDuringInitialTreeLoad = false;
        dispatch(set_selected_distribution_network_refresh_status('fail'));
      }
    }

    // Only on a cold load, but there it revalidates every distribution rather
    // than only the stale ones — a reload refreshes the whole tree, matching
    // what it already does for the selected one. Every field / farming-unit /
    // distribution mutation ends in load_distributions(true), and sweeping on
    // each save would be pure waste — and would put a bulk pass in flight next
    // to the edit's own by-id reload.
    if (!force_load) {
      scheduleBackgroundFullHydration(
        dispatch,
        requestId,
        { force: true, includeSelected: servedFromCache },
        selectedTreeSettled
      );
    }
  };
};

export var load_distribution_by_id = distributionId => {
  return async (dispatch, getState) => {
    const state = getState();
    const existing =
      state.distribution_data.distributions?.distributions?.[distributionId];
    // A skeleton entry already has farming_units (id/name/role only), so the
    // presence of farming_units is not enough — only skip the fetch when the
    // full tree (fields on every farming unit) is already loaded.
    // Hydrating what is missing is all this does. Keeping already-loaded trees
    // fresh is the background sweep's job — staleness is wall-clock, so it is
    // never limited to the one distribution the user happens to open, and one
    // paginated pass covers every stale tree instead of a round trip each.
    if (distributionManager.isFullDistributionTree(existing)) {
      return existing;
    }

    // The user is waiting on this one, so fetch it directly rather than waiting
    // out a bulk sweep.
    let loadPromise = pendingDistributionTreeLoads[distributionId];
    if (!loadPromise) {
      loadPromise = (async () => {
        try {
          const tree =
            await distributionManager.getDistributionById(distributionId);
          const merged = mergeDistributionsResult(getState(), {
            distributions: {
              [distributionId]: tree,
            },
          });
          dispatch(set_all_distributions(merged));
          markDistributionTreeFresh(distributionId);
          // null currentId: a lazy hydration must not move the persisted
          // "which distribution was selected" pointer.
          schedulePersistDistributionsCache(getState, null);
          return merged.distributions[distributionId];
        } finally {
          if (pendingDistributionTreeLoads[distributionId] === loadPromise) {
            delete pendingDistributionTreeLoads[distributionId];
          }
        }
      })();
      pendingDistributionTreeLoads[distributionId] = loadPromise;
    }

    return loadPromise;
  };
};

// Which distributions the bulk pass should hydrate, evaluated against current
// state. Deliberately called twice — once to decide whether to fetch at all, and
// again at merge time — because the world moves while five pages are in flight.
//
// `force` refreshes hydrated trees regardless of age: a page reload revalidates
// everything, the same way it already refetches the selected tree by id whether
// or not the TTL says it is stale. Without it a reload on a warm cache asked for
// nothing at all, since persisting the whole tree leaves every distribution full
// and (under an hour) fresh.
const selectBulkHydrationIds = (
  state,
  { force = false, includeSelected = false } = {}
) => {
  const distributionsMap =
    state.distribution_data.distributions?.distributions || {};
  const selectedId =
    state.distribution_data.selected_entities?.distribution?.id;

  return Object.keys(distributionsMap).filter(id => {
    // An in-flight by-id load is the authoritative one (a user switching
    // distributions mid-bulk, or the reload that follows an edit). Never race it:
    // mergeDistributionsResult replaces an entry wholesale, so a late bulk page
    // would undo it.
    if (pendingDistributionTreeLoads[id]) {
      return false;
    }

    // A save the server has not confirmed back yet always beats this snapshot.
    // Deliberately ahead of both checks below: the selected-distribution skip is
    // switched off on a cache-served boot, and the staleness rule cannot help
    // when staleness is the very reason the sweep is running. This is the only
    // guard that holds in all three cases.
    if (pendingLocalWrites.has(id)) {
      return false;
    }

    const isFull = distributionManager.isFullDistributionTree(
      distributionsMap[id]
    );

    const stale = isFullDistributionTreeStale(id);

    // A hydrated, still-fresh selected distribution is normally owned by its own
    // by-id path. Letting a bulk page overwrite it there would clobber a
    // just-saved field edit, and a bulk tree carries no historical_data, so it
    // would also blank any open graph. Every write path marks it fresh, so
    // "fresh" is exactly the window that needs protecting; once it goes stale
    // nothing local is newer than the server.
    //
    // includeSelected lifts it, and only the cache-served boot passes it: there
    // the selected tree got no by-id fetch, so the sweep is the only thing that
    // would ever revalidate it. A cold load must NOT pass it — it just fetched
    // that tree by id and started its history loads, and a bulk tree carries no
    // historical_data.
    if (!includeSelected && isFull && !stale && id === selectedId) {
      return false;
    }

    return !isFull || stale || force;
  });
};

// Cross-distribution screens (technician assistant, transmitter search, remote
// configuration modal) need every distribution's full field tree, not just the
// selected one — they resolve fields and sensors by id across all customers.
// With the skeleton-first load only the selected distribution is hydrated, so
// these screens call this on open to fill in the rest. It also runs once per
// cold load to warm the persisted cache in the background.
//
// Trees that are full and still fresh are never refetched unless `force` is set:
// when nothing qualifies this returns immediately, so reopening a screen costs
// nothing. The cold load passes force so a reload revalidates the whole tree.
export var ensure_all_distributions_loaded = (options = {}) => {
  return async (dispatch, getState) => {
    if (!selectBulkHydrationIds(getState(), options).length) {
      return;
    }
    // One sweep at a time. Every stale distribution the user opens asks for a
    // refresh, and both the boot path and a cross-distribution screen can ask
    // too — without this they would each start their own five-page pass.
    if (!pendingBulkHydration) {
      pendingBulkHydration = runBulkHydration(
        dispatch,
        getState,
        options
      ).finally(() => {
        pendingBulkHydration = null;
      });
    }
    return pendingBulkHydration;
  };
};

let pendingBulkHydration = null;

// The bulk route does not return historical_data, and mergeDistributionsResult
// replaces a distribution wholesale — so a sweep landing on a tree whose graphs
// are already open would blank them. Carry the real-time data we already hold
// (farming-unit and field level, matching where the reducers write it) across
// onto the incoming tree.
const preserveHistoricalData = (incoming, existing) => {
  if (!existing?.farming_units || !incoming?.farming_units) {
    return incoming;
  }

  const farming_units = {};
  for (const fuId of Object.keys(incoming.farming_units)) {
    const incomingFu = incoming.farming_units[fuId];
    const existingFu = existing.farming_units[fuId];
    if (!existingFu) {
      farming_units[fuId] = incomingFu;
      continue;
    }

    const fields = {};
    for (const fieldId of Object.keys(incomingFu.fields || {})) {
      const carried = existingFu.fields?.[fieldId]?.historical_data;
      fields[fieldId] = carried
        ? { ...incomingFu.fields[fieldId], historical_data: carried }
        : incomingFu.fields[fieldId];
    }

    farming_units[fuId] = {
      ...incomingFu,
      ...(incomingFu.fields ? { fields } : {}),
      ...(existingFu.historical_data
        ? { historical_data: existingFu.historical_data }
        : {}),
    };
  }

  return { ...incoming, farming_units };
};

const runBulkHydration = async (dispatch, getState, options) => {
  // Bulk path: the legacy paginated route, with the meteorological station
  // scan turned off — these screens never read meteorological_sources, and
  // that scan is the single most expensive part of the old payload.
  // Everything below is read from the server as it was at this instant.
  const startedAt = Date.now();

  try {
    const bulk = await distributionManager.distributions(1, 5, false);
    const bulkMap = bulk?.distributions || {};

    const currentMap =
      getState().distribution_data.distributions?.distributions || {};
    const trees = {};
    for (const id of selectBulkHydrationIds(getState(), options)) {
      // Anything refetched while these five pages were in flight is newer than
      // what they carry, so applying them would move that tree backwards. This
      // is what saves a field edit: the save reloads its distribution by id,
      // which stamps it, and a sweep that started first must not overwrite the
      // result with the pre-save copy it fetched. The in-flight and freshness
      // checks above cannot see this, since by then the fetch has finished and
      // includeSelected deliberately ignores freshness.
      const refetchedSinceStart = distributionTreeFetchedAt.get(id) > startedAt;
      if (bulkMap[id] && !refetchedSinceStart) {
        trees[id] = preserveHistoricalData(bulkMap[id], currentMap[id]);
      }
    }

    if (Object.keys(trees).length) {
      dispatch(
        set_all_distributions(
          mergeDistributionsResult(getState(), { distributions: trees })
        )
      );
      Object.keys(trees).forEach(id => markDistributionTreeFresh(id));
      // Once for the whole batch, not per id.
      schedulePersistDistributionsCache(getState, null);
      return;
    }
  } catch (err) {
    console.error('[Distributions] bulk load failed:', err.message);
  }

  // Bulk failed or came back empty — fall back to per-distribution loads so
  // the screen still works rather than silently showing nothing. Isolate each
  // one: a single distribution the client cannot parse must not reject the whole
  // batch and take every other tree down with it, and an unrejected promise here
  // surfaces to the user as an uncaught error.
  await Promise.all(
    selectBulkHydrationIds(getState(), options).map(id =>
      Promise.resolve(dispatch(load_distribution_by_id(id))).catch(err =>
        console.error(
          `[Distributions] hydration failed for ${id}:`,
          err.message
        )
      )
    )
  );
};

export var selected_entities = (distribution, farming_unit, field, sensors) => {
  return async (dispatch, getState) => {
    var state = getState();
    let is_archive_mode = state.distribution_data.field_state === 'archive';

    // Check if URL params are present - if so, skip localStorage selection and url params will be used
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams =
        urlParams.has('distributionId') && urlParams.has('farmingUnitId');
      if (hasUrlParams && !distribution && !farming_unit && !field) {
        // URL params exist and no explicit entities passed - skip localStorage loading
        // selected_entities_by_id will be called shortly with the correct entities
        return;
      }
    }

    // Only await AsyncStorage if we need the cached values
    // We only need cached values if there's no current Redux state AND no explicit parameters
    let cachedDistributionId = null;
    let cachedFarmingUnitId = null;
    let cachedFieldId = null;
    let cachedSensorIds = null;

    const hasCurrentState =
      state.distribution_data.selected_entities.distribution ||
      state.distribution_data.selected_entities.farming_unit ||
      state.distribution_data.selected_entities.field;

    const hasExplicitParams = distribution || farming_unit || field || sensors;

    // Only block on AsyncStorage if we have no current state and no explicit parameters
    // (i.e., app startup or first load)
    const needsCachedValues = !hasCurrentState && !hasExplicitParams;

    if (needsCachedValues) {
      if (state.distribution_data.pending_push_selection) {
        return;
      }

      // Only block on AsyncStorage if we actually need the cached values
      [
        cachedDistributionId,
        cachedFarmingUnitId,
        cachedFieldId,
        cachedSensorIds,
      ] = await Promise.all([
        getItem('SELECTED_DISTRIBUTION_ID', null),
        getItem('SELECTED_FARMING_UNIT_ID', null),
        getItem('SELECTED_FIELD_ID', null),
        getItem('SELECTED_SENSOR_TRANSMITTER_IDS', null),
      ]);

      state = getState();
      if (state.distribution_data.pending_push_selection) {
        return;
      }
      // Note: History loading is deferred until after entities are validated and set
      // to avoid race conditions. See shouldLoadSelectedFarmingUnitData logic below.
    }

    // Distribution
    var selected_distribution = distribution;

    if (!selected_distribution) {
      var selected_distribution_id = undefined;

      if (state.distribution_data.selected_entities.distribution) {
        selected_distribution_id =
          state.distribution_data.selected_entities.distribution.id;
      } else if (cachedDistributionId != null) {
        selected_distribution_id = cachedDistributionId;
      }
      if (state.distribution_data?.distributions?.distributions === undefined) {
        selected_distribution = null;
      } else if (
        selected_distribution_id in
        state.distribution_data?.distributions?.distributions
      ) {
        selected_distribution =
          state.distribution_data.distributions.distributions[
            selected_distribution_id
          ];
      } else {
        for (var first_distribution_id in state.distribution_data.distributions
          .distributions)
          break; // Take the first distribution
        selected_distribution =
          state.distribution_data.distributions.distributions[
            first_distribution_id
          ];
      }

      if (
        !selected_distribution &&
        state.distribution_data?.distributions?.distributions
      ) {
        return;
      }
    }

    // Farming Unit
    var selected_farming_unit = farming_unit;

    if (!selected_farming_unit && selected_distribution) {
      var selected_farming_unit_id = undefined;

      if (state.distribution_data.selected_entities.farming_unit) {
        selected_farming_unit_id =
          state.distribution_data.selected_entities.farming_unit.id;
      } else if (cachedFarmingUnitId != null) {
        selected_farming_unit_id = cachedFarmingUnitId;
      } else {
        // Deep links / by-id may have written the intended FU while selection
        // only held a skeleton (or null FU). Read it so the full-tree reselect
        // can finish resolving after GET distribution/{id}.
        selected_farming_unit_id = await getItem(
          'SELECTED_FARMING_UNIT_ID',
          null
        );
      }

      if (
        selected_farming_unit_id &&
        selected_farming_unit_id in selected_distribution.farming_units
      ) {
        selected_farming_unit =
          selected_distribution.farming_units[selected_farming_unit_id];
      } else {
        for (var first_farming_unit_id in selected_distribution.farming_units)
          break; // Take the first farming unit
        selected_farming_unit =
          selected_distribution.farming_units[first_farming_unit_id];
      }
    }

    // Never commit a skeleton FU (no `fields`) into selected_entities — the
    // same rule as optimistic distribution switch. Keep the intended id in
    // storage; the next full-tree selected_entities call resolves it.
    if (selected_farming_unit && !selected_farming_unit.fields) {
      if (selected_farming_unit.id) {
        setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
      }
      selected_farming_unit = null;
    }

    // Field
    var selected_field = field;

    // A skeleton farming unit (from the structural skeleton load, not yet
    // hydrated with a full distribution tree) has no `fields` key at all.
    // Treat that as "not ready to resolve a field" rather than crashing on
    // Object.entries(undefined) — the field resolves once the full tree lands.
    if (
      !selected_field &&
      selected_farming_unit &&
      selected_farming_unit.fields
    ) {
      var selected_field_id = undefined;

      if (state.distribution_data.selected_entities.field) {
        selected_field_id = state.distribution_data.selected_entities.field.id;
      } else if (cachedFieldId != null) {
        selected_field_id = cachedFieldId;
      } else {
        // Monday / URL deep links persist the field id before the tree is
        // full; pick it up on the post-hydration reselect.
        selected_field_id = await getItem('SELECTED_FIELD_ID', null);
      }

      // filter just active fields from farmingUnit fields ( field_state is active )
      let filtered_ids = Object.fromEntries(
        Object.entries(selected_farming_unit.fields).filter(
          ([k, field]) => field.settings.field_state === 'active'
        )
      );

      if (is_archive_mode) {
        // when we toggle to archive mode , filter to archive fields only !
        filtered_ids = Object.fromEntries(
          Object.entries(selected_farming_unit.fields).filter(
            ([k, field]) => field.settings.field_state === 'archive'
          )
        );
      }

      if (selected_field_id && selected_field_id in filtered_ids) {
        selected_field = selected_farming_unit.fields[selected_field_id];
      } else {
        for (var first_field_id in filtered_ids) break; // Take the first field
        selected_field = selected_farming_unit.fields[first_field_id];
      }
    }

    let shouldLoadSelectedFarmingUnitData = false;
    let shouldLoadSelectedFieldData = false;
    const currentRecommendations =
      getState().distribution_data.farming_unit_recommendations.recommendations;
    const userLengthUnits = getState().authentication_data.user_data.length;
    const fieldRecommendation =
      selected_field &&
      currentRecommendations &&
      currentRecommendations[selected_field.id];
    const hasRecommendationsForField =
      fieldRecommendation && fieldRecommendation.units === userLengthUnits;
    if (
      (selected_farming_unit &&
        !isFarmingUnitHistoryLoading(getState, selected_farming_unit.id)) ||
      (isSupportingPlannerByWeeks(selected_field) &&
        !hasRecommendationsForField)
    ) {
      // and no history for this field
      // load field history
      shouldLoadSelectedFarmingUnitData = true;
    }

    // Defer history loading to avoid blocking navigation
    // Use setTimeout to make history loading non-blocking
    if (
      shouldLoadSelectedFarmingUnitData &&
      selected_distribution &&
      selected_farming_unit &&
      !deferHistoryDuringInitialTreeLoad
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      setTimeout(() => {
        dispatchHistoryLoads(dispatch, distId, fuId);
      }, 0);
      shouldLoadSelectedFieldData = true;
    }

    // Optimized: Compare by ID instead of expensive JSON.stringify
    if (
      selected_field &&
      (!state.distribution_data.selected_entities.field ||
        state.distribution_data.selected_entities.field.id !==
          selected_field.id)
    ) {
      shouldLoadSelectedFieldData = true;

      // Create new sensors object to avoid mutating Redux state
      const updatedSensors = Object.values(selected_field.sensors).reduce(
        (acc, currentSensor) => {
          acc[currentSensor.id] = {
            ...currentSensor,
            state: GeneralClasses.SENSOR_STATES.INIT,
          };
          return acc;
        },
        {}
      );
      selected_field = {
        ...selected_field,
        sensors: updatedSensors,
      };
    }

    if (
      shouldLoadSelectedFieldData &&
      selected_distribution &&
      selected_farming_unit &&
      selected_field
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      const fId = selected_field.id;
      setTimeout(() => {
        dispatch(get_field_history(distId, fuId, fId));
      }, 0);
    }

    // Sensor
    var selected_sensors = sensors;

    if (!selected_sensors && selected_field) {
      var selected_sensor_transmitter_ids = undefined;

      if (cachedSensorIds != null) {
        selected_sensor_transmitter_ids = cachedSensorIds.split(',');

        let field_transmitter_ids = sensorsManager.get_transmitter_ids(
          selected_field.sensors
        );

        // find the stored sensors objects in the current field instance
        let checkSensorsArray = selected_sensor_transmitter_ids.map(
          function (sensor_id) {
            if (field_transmitter_ids.includes(sensor_id)) {
              let selectedSensor = Object.values(selected_field.sensors).filter(
                a => a.transmitter_id == sensor_id
              );
              return selectedSensor;
            } else {
              return undefined;
            }
          }
        );

        if (checkSensorsArray.includes(undefined)) {
          selected_sensors = {};
        } else {
          selected_sensors = selected_sensor_transmitter_ids.reduce(function (
            map,
            obj
          ) {
            let sensor = Object.values(selected_field.sensors).filter(
              a => a.transmitter_id == obj
            )[0];
            map[sensor.id] = sensor;
            return map;
          }, {});
        }
      } else {
        selected_sensors = {};
      }
    }

    // Don't await setItem - fire and forget to avoid blocking UI
    if (selected_distribution) {
      setItem('SELECTED_DISTRIBUTION_ID', selected_distribution.id);
    }
    if (selected_farming_unit) {
      setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
    }
    if (selected_field) {
      setItem('SELECTED_FIELD_ID', selected_field.id);
    }

    let field_transmitter_ids =
      sensorsManager.get_transmitter_ids(selected_sensors);
    if (selected_sensors) {
      setItem('SELECTED_SENSOR_TRANSMITTER_IDS', field_transmitter_ids);
    } else {
      setItem('SELECTED_SENSORS_ID', null);
    }

    var mixpanelInstance = getMixpanelInstance();
    mixpanelInstance.register({
      Distribution: selected_distribution ? selected_distribution.name : null,
      'Distribution Id': selected_distribution
        ? selected_distribution.id
        : null,
      'Farming Unit': selected_farming_unit ? selected_farming_unit.name : null,
      'Farming Unit Id': selected_farming_unit
        ? selected_farming_unit.id
        : null,
      Field: selected_field ? selected_field.name : null,
      'Field Id': selected_field ? selected_field.id : null,
      'Early Season': fieldManager.isFieldEarlyDays(selected_field),
      'Crop Type': selected_field ? selected_field.crop_data.crop_type : null,
      'Soil Type': selected_field ? selected_field.soil_type : null,
      // 'Sensor Ids': selected_sensors ? selected_sensors.map(a => a.id) : null,
    });

    // v3.x API: use getPeople() instead of people
    mixpanelInstance.getPeople().set({
      'Last Distribution': selected_distribution
        ? selected_distribution.name
        : null,
      'Last Farming Unit': selected_farming_unit
        ? selected_farming_unit.name
        : null,
      'Last Field': selected_field ? selected_field.name : null,
    });

    dispatch(
      set_selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        selected_sensors
      )
    );
  };
};

export var postDistributionLoadAnalytics = distributions => {
  let mixpanelInstance = getMixpanelInstance();
  let lastInstallationDate = getFirstInstallationDate(
    distributions.distributions
  );
  let formattedInstallationTime = lastInstallationDate.format(
    'YYYY-MM-DD HH:mm:ss'
  );

  // v3.x API: use getPeople() instead of people
  mixpanelInstance.getPeople().set({
    'First Installation Date': formattedInstallationTime,
  });

  let allUserCrops = getFieldCrops(distributions.distributions);
  mixpanelInstance.getPeople().union('All user crops', {
    Crops: allUserCrops,
  });
};

export var selected_entities_by_id = (
  distribution_id,
  farming_unit_id,
  field_id = null
) => {
  return (dispatch, getState) => {
    var state = getState();

    // Persist intended ids immediately so a later full-tree selected_entities
    // reselect (after skeleton-first load) still lands on the deep-linked
    // field — e.g. Monday links with ?distributionId&farmingUnitId&fieldId.
    if (distribution_id) {
      setItem('SELECTED_DISTRIBUTION_ID', distribution_id);
    }
    if (farming_unit_id) {
      setItem('SELECTED_FARMING_UNIT_ID', farming_unit_id);
    }
    if (field_id) {
      setItem('SELECTED_FIELD_ID', field_id);
    }

    var selected_distribution = null;
    var selected_farming_unit = null;
    var selected_field = null;

    var all_distributions = state.distribution_data.distributions.distributions;
    if (all_distributions && distribution_id in all_distributions) {
      selected_distribution = all_distributions[distribution_id];
    }

    if (
      selected_distribution &&
      farming_unit_id in selected_distribution.farming_units
    ) {
      selected_farming_unit =
        selected_distribution.farming_units[farming_unit_id];
    }

    // Skeleton FUs omit `fields` — do not pass them into selected_entities
    // (it would either crash or commit a useless FU). Keep dist-only until
    // the full tree lands; storage ids above finish the resolve then.
    if (!selected_farming_unit?.fields) {
      if (selected_distribution) {
        dispatch(
          set_selected_entities(selected_distribution, null, null, null)
        );
      }
      if (distribution_id) {
        dispatch(load_distribution_by_id(distribution_id));
      }
      return;
    }

    if (field_id && field_id in selected_farming_unit.fields) {
      selected_field = selected_farming_unit.fields[field_id];
    }

    dispatch(
      distributionActions.selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        null
      )
    );
  };
};

export var delete_distribution = distribution_id => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.delete_distribution(distribution_id).then(
        function (result) {
          dispatch(distribution_action_success('DELETE'));
          reloadAfterLocalWrite(dispatch, distribution_id);
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('DELETE', err));
        }
      );
    }
  };
};

export var update_distribution = (distribution_id, distribution_name) => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager
        .update_distribution(distribution_id, distribution_name)
        .then(
          function (result) {
            dispatch(distribution_action_success('UPDATE'));
            reloadAfterLocalWrite(dispatch, distribution_id);
          },
          function (err) {
            console.log(err);
            dispatch(distribution_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_distribution = distribution_name => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('CREATE'));

    if (!getState().authentication_data.user_details.is_root) {
      dispatch(
        distribution_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create distributions"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.create_distribution(distribution_name).then(
        function (result) {
          dispatch(distribution_action_success('CREATE'));
          // The only write that does not go through reloadAfterLocalWrite:
          // there is no existing tree for a sweep to overwrite, and no id to
          // hold it off with until the reload brings one back.
          dispatch(load_distributions(true));
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('CREATE', err));
        }
      );
    }
  };
};

export var set_field_state = field_state => {
  return (dispatch, getState) => {
    dispatch(set_field_state_action(field_state));
    dispatch(selected_entities(null, null, null, null));
  };
};

export var initiate_distribution_action = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_INIT',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_success = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_SUCCESS',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_fail = (distribution_action_type, err) => {
  return {
    type: 'DISTRIBUTION_ACTION_FAIL',
    distribution_action_type: distribution_action_type,
    error_message: error_message(err),
  };
};

export var clear_distribution_action = () => {
  return {
    type: 'CLEAR_DISTRIBUTION_ACTION',
  };
};

export var clear_distribution_data = () => {
  // Logout: requests still in flight belong to the previous session.
  clearPendingHistoryLoads();
  return {
    type: 'CLEAR_DISTRIBUTION_DATA',
  };
};

// ======

export var delete_farming_unit = (distribution_id, farming_unit_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this farming unit"
          )
        )
      );
    } else {
      // delete farming unit
      farmingUnitManager
        .delete_farming_unit(distribution_id, farming_unit_id)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('DELETE'));
            reloadAfterLocalWrite(dispatch, distribution_id);
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit.id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this farming unit"
          )
        )
      );
    } else {
      // update farming unit
      farmingUnitManager
        .update_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('UPDATE'));

            // Optimistically merge the saved farming unit (with its sensors)
            // into Redux immediately, so newly added sensors show right away
            // instead of only after the load_distributions re-pull lands past
            // GSI propagation. The re-pull below reconciles ids (tmp_ -> SC_N).
            dispatch(
              update_farming_unit_optimistic(distribution_id, farming_unit)
            );

            reloadAfterLocalWrite(dispatch, distribution_id);
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('CREATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a farming unit under this distribution"
          )
        )
      );
    } else {
      // create farming unit
      farmingUnitManager
        .create_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('CREATE'));
            reloadAfterLocalWrite(dispatch, distribution_id);
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('CREATE', err));
          }
        );
    }
  };
};

/**
 * Refresh selected_entities with the latest data from distributions state.
 * Call after updating historical data so components that read from
 * selected_entities see the new data without requiring a field re-selection.
 */
function refreshSelectedEntities(
  dispatch,
  getState,
  distributionId,
  farmingUnitId
) {
  const state = getState();
  const selectedEntities = state.distribution_data.selected_entities;
  if (
    !selectedEntities.farming_unit ||
    selectedEntities.farming_unit.id !== farmingUnitId
  ) {
    return;
  }

  const updatedFarmingUnit =
    state.distribution_data.distributions?.distributions?.[distributionId]
      ?.farming_units?.[farmingUnitId];

  if (!updatedFarmingUnit) return;

  const selectedField = selectedEntities.field;
  const updatedField =
    selectedField && updatedFarmingUnit.fields?.[selectedField.id]
      ? updatedFarmingUnit.fields[selectedField.id]
      : selectedField;

  dispatch(
    set_selected_entities(
      selectedEntities.distribution,
      updatedFarmingUnit,
      updatedField,
      selectedEntities.sensors
    )
  );
}

// Concurrent calls for one farming unit share a single request. Pass
// { force: true } after a write or a units change: it starts a new request
// and an older one still in flight is dropped when it lands.
export var get_farming_units_history = (
  distribution_id,
  farming_unit_id,
  field_id,
  options
) => {
  return (dispatch, getState) => {
    if (!farming_unit_id) {
      return Promise.resolve();
    }

    return runHistoryLoad(
      pendingFarmingUnitHistoryLoads,
      distribution_id + '/' + farming_unit_id,
      options?.force,
      isCurrent => {
        dispatch(initiate_farming_unit_history_load_action(farming_unit_id));

        return farmingUnitManager
          .loadFarmingUnitHistory(distribution_id, farming_unit_id, field_id)
          .then(
            function (result) {
              // Superseded by a forced load, which owns the data and status.
              if (!isCurrent()) {
                return;
              }

              let currentFarmingUnit =
                getState().distribution_data.distributions?.distributions?.[
                  distribution_id
                ]?.farming_units?.[farming_unit_id];

              // Guard: If farming unit not found, just clear status and return
              if (!currentFarmingUnit) {
                console.log(
                  'get_farming_units_history: farming unit not found in state'
                );
                dispatch(
                  clear_status_farming_unit_history_load_action(farming_unit_id)
                );
                return;
              }

              dispatch(
                update_farming_unit_historical_data(
                  distribution_id,
                  farming_unit_id,
                  result['historicalData']
                )
              );

              refreshSelectedEntities(
                dispatch,
                getState,
                distribution_id,
                farming_unit_id
              );

              dispatch(
                success_farming_unit_history_load_action(farming_unit_id)
              );
              dispatch(
                clear_status_farming_unit_history_load_action(farming_unit_id)
              );
            },
            function (err) {
              console.log('get_farming_units_history error:', err);
              if (isCurrent()) {
                dispatch(
                  clear_status_farming_unit_history_load_action(farming_unit_id)
                );
              }
            }
          );
      }
    );
  };
};

// Same dedup as get_farming_units_history, per field.
export var get_field_history = (
  distribution_id,
  farming_unit_id,
  field_id,
  options
) => {
  return (dispatch, getState) => {
    if (!field_id) {
      return Promise.resolve();
    }

    return runHistoryLoad(
      pendingFieldHistoryLoads,
      distribution_id + '/' + farming_unit_id + '/' + field_id,
      options?.force,
      isCurrent => {
        dispatch(initiate_field_history_load_action(field_id));

        return fieldManager
          .loadFieldHistory(distribution_id, farming_unit_id, field_id)
          .then(
            function (result) {
              // Superseded by a forced load, which owns the data and status.
              if (!isCurrent()) {
                return;
              }

              const historicalData = result['historicalData'][field_id];

              dispatch(
                update_field_historical_data(
                  distribution_id,
                  farming_unit_id,
                  field_id,
                  historicalData
                )
              );

              refreshSelectedEntities(
                dispatch,
                getState,
                distribution_id,
                farming_unit_id
              );

              dispatch(success_field_history_load_action(field_id));
              dispatch(clear_status_field_history_load_action(field_id));
            },
            function (err) {
              console.log('get_field_history error:', err);
              if (isCurrent()) {
                dispatch(clear_status_field_history_load_action(field_id));
              }
            }
          );
      }
    );
  };
};

// ======

export var initiate_farming_unit_action = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_INIT',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_success = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_SUCCESS',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_fail = (farming_unit_action_type, err) => {
  return {
    type: 'FARMING_UNIT_ACTION_FAIL',
    farming_unit_action_type: farming_unit_action_type,
    error_message: error_message(err),
  };
};

export var clear_farming_unit_action = () => {
  return {
    type: 'CLEAR_FARMING_UNIT_ACTION',
  };
};

export var initiate_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_INIT',
    farming_unit_id: farming_unit_id,
  };
};

export var initiate_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_INIT',
    field_id: field_id,
  };
};

export var clear_farming_unit_history_load_action = () => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR',
    farming_unit_id: '',
  };
};

// Without an id it clears the status for every farming unit (legacy callers).
export var clear_status_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR_STATUS',
    farming_unit_id: farming_unit_id,
  };
};

// Without an id it clears the status for every field (legacy callers).
export var clear_status_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_CLEAR_STATUS',
    field_id: field_id,
  };
};

export var update_field_historical_data = (
  distributionId,
  farmingUnitId,
  fieldId,
  historicalData
) => {
  return {
    type: 'UPDATE_FIELD_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, fieldId, historicalData },
  };
};

export var update_farming_unit_historical_data = (
  distributionId,
  farmingUnitId,
  historicalData
) => {
  return {
    type: 'UPDATE_FARMING_UNIT_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, historicalData },
  };
};

// Optimistically update field data in Redux immediately after API success,
// before load_distributions completes (which can take ~10 seconds)
export var update_field_optimistic = (
  distributionId,
  farmingUnitId,
  fieldObject
) => {
  return {
    type: 'UPDATE_FIELD_OPTIMISTIC',
    payload: { distributionId, farmingUnitId, fieldObject },
  };
};

export var update_farming_unit_optimistic = (distributionId, farmingUnit) => {
  return {
    type: 'UPDATE_FARMING_UNIT_OPTIMISTIC',
    payload: { distributionId, farmingUnit },
  };
};

export var success_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_SUCCESS',
    farming_unit_id: farming_unit_id,
  };
};
export var success_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_SUCCESS',
    field_id: field_id,
  };
};

export var delete_field = (distribution_id, farming_unit_id, field_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this field"
          )
        )
      );
    } else {
      // delete farming unit
      fieldManager
        .delete_field(distribution_id, farming_unit_id, field_id)
        .then(
          function (result) {
            dispatch(field_action_success('DELETE'));
            reloadAfterLocalWrite(dispatch, distribution_id);

            var mixpanelInstance = getMixpanelInstance();

            mixpanelInstance.track('Field Delete');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      ) &&
      !farmingUnitManager.is_moderator(
        getState().distribution_data.selected_entities.farming_unit
      )
    ) {
      dispatch(
        field_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this field"
          )
        )
      );
    } else {
      // update field
      fieldManager
        .update_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('UPDATE'));

            // Optimistically update Redux state with the field data we just saved,
            // so the user sees the updated data immediately while load_distributions runs
            dispatch(
              update_field_optimistic(
                distribution_id,
                farming_unit_id,
                field_object
              )
            );

            reloadAfterLocalWrite(dispatch, distribution_id);

            var mixpanelInstance = getMixpanelInstance();
            mixpanelInstance.track('Field Update');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    var state = getState();

    dispatch(initiate_field_action('CREATE'));

    if (
      !distributionManager.is_admin(
        state.distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a field under this farming unit"
          )
        )
      );
    } else {
      // create field
      fieldManager
        .create_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('CREATE'));
            // Argument list kept verbatim, including the trailing extra one the
            // 5-parameter signature ignores — not this change's business.
            reloadAfterLocalWrite(
              dispatch,
              distribution_id,
              false,
              null,
              null,
              result['fieldId'],
              null
            );

            mixpanel.track('Field Create', {
              Field: result['fieldName'],
              'Field Id': result['fieldId'],
            });
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('CREATE', err));
          }
        );
    }
  };
};

export var initiate_field_action = field_action_type => {
  return {
    type: 'FIELD_ACTION_INIT',
    field_action_type: field_action_type,
  };
};

export var field_action_success = field_action_type => {
  return {
    type: 'FIELD_ACTION_SUCCESS',
    field_action_type: field_action_type,
  };
};

export var field_action_fail = (field_action_type, err) => {
  return {
    type: 'FIELD_ACTION_FAIL',
    field_action_type: field_action_type,
    error_message: error_message(err),
  };
};

export var clear_field_action = () => {
  return {
    type: 'CLEAR_FIELD_ACTION',
  };
};

export var set_selected_entities = (
  selected_distribution,
  selected_farming_unit,
  selected_field,
  selected_sensors
) => {
  return {
    type: 'SET_SELECTED_ENTITIES',
    selected_distribution: selected_distribution,
    selected_farming_unit: selected_farming_unit,
    selected_field: selected_field,
    selected_sensors: selected_sensors,
  };
};

export var set_pending_push_selection = pending_push_selection => {
  return {
    type: 'SET_PENDING_PUSH_SELECTION',
    pending_push_selection,
  };
};

export var clear_pending_push_selection = () => {
  return {
    type: 'CLEAR_PENDING_PUSH_SELECTION',
  };
};

export var set_all_distributions = distributions => {
  return {
    type: 'SET_ALL_DISTRIBUTIONS',
    distributions: distributions,
  };
};

export const set_transmitter_coordinates = transmitter_coordinates => {
  return {
    type: 'SET_TRANSMITTER_COORDINATES',
    transmitter_coordinates,
  };
};

export var set_field_state_action = field_state => {
  return {
    type: 'SET_FIELD_STATE',
    field_state: field_state,
  };
};

export var set_first_all_distributions_load_status = status => {
  return {
    type: 'SET_FIRST_ALL_DISTRIBUTIONS_LOAD_STATUS',
    status: status,
  };
};

export var set_selected_distribution_network_refresh_status = status => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION_NETWORK_REFRESH_STATUS',
    status,
  };
};

export const set_selected_farming_unit = selected_farming_unit => {
  return {
    type: 'SET_SELECTED_FARMING_UNIT',
    selected_farming_unit,
  };
};

export const set_selected_distribution = selected_distribution => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION',
    selected_distribution,
  };
};

// A cold load dispatches this alongside the history loads, before the full
// distribution tree has landed — at that point the farming unit is missing or
// skeleton-only and its fields carry no planner settings, so there is nothing
// to decide on yet. Retry until the tree arrives instead of giving up: without
// this, All Farm View shows no recommendations until the user re-selects a
// farming unit (which re-dispatches this thunk against a populated tree).
const RECOMMENDATIONS_TREE_RETRY_DELAY_MS = 500;
const RECOMMENDATIONS_TREE_MAX_RETRIES = 12;

const isFarmingUnitTreeReady = farmingUnit => {
  const fields = farmingUnit && farmingUnit.fields;
  if (!fields) return false;
  // Fields exist but a skeleton tree carries no settings — only once at least
  // one field has planner_settings can the weekly filter below be trusted.
  return Object.values(fields).some(
    field => field && field.settings && field.settings.planner_settings
  );
};

export const get_farming_units_recommendations = (
  distribution_id,
  farming_unit_id,
  attempt = 0
) => {
  return async (dispatch, getState) => {
    const state = getState();
    // Guard every hop: the distributions map can be null (e.g. just after
    // clear_distributions_cache on a unit-system change) and the distribution /
    // farming unit may be skeleton-only. Matches get_farming_units_history.
    const currentFarmingUnit =
      state.distribution_data.distributions?.distributions?.[distribution_id]
        ?.farming_units?.[farming_unit_id];

    if (
      !isFarmingUnitTreeReady(currentFarmingUnit) &&
      attempt < RECOMMENDATIONS_TREE_MAX_RETRIES
    ) {
      setTimeout(() => {
        dispatch(
          get_farming_units_recommendations(
            distribution_id,
            farming_unit_id,
            attempt + 1
          )
        );
      }, RECOMMENDATIONS_TREE_RETRY_DELAY_MS);
      return;
    }

    // Out of retries with still no fields — nothing to fetch. Leave whatever is
    // in the store alone rather than overwriting it with an empty map.
    if (!currentFarmingUnit || !currentFarmingUnit.fields) return;

    // A retry can land after the user moved on. The store keeps one
    // recommendations map for the whole app, so dispatching a late result for a
    // farming unit that is no longer selected would wipe the visible one.
    if (attempt > 0) {
      const selectedFarmingUnitId =
        getState().distribution_data.selected_entities?.farming_unit?.id;
      if (selectedFarmingUnitId && selectedFarmingUnitId !== farming_unit_id) {
        return;
      }
    }

    const fieldIds = Object.keys(currentFarmingUnit.fields);
    const recommendations = {};

    await Promise.all(
      fieldIds.map(async fieldId => {
        try {
          if (
            currentFarmingUnit.fields[fieldId]?.settings?.planner_settings
              ?.type === plannerType.WEEKLY_RECOMMENDATION
          ) {
            const fieldRec = await getRecommendation(fieldId);
            recommendations[fieldId] = fieldRec;
          }
        } catch (e) {
          recommendations[fieldId] = null;
        }
      })
    );

    dispatch({
      type: 'FARMING_UNIT_RECOMMENDATIONS_LOADED',
      payload: { farmingUnitId: farming_unit_id, recommendations },
    });
  };
};

export const update_field_recommendation_show_rate_line = (
  fieldId,
  showRateLine
) => {
  return {
    type: 'UPDATE_FIELD_RECOMMENDATION_SHOW_RATE_LINE',
    payload: { fieldId, showRateLine },
  };
};
